import { useCallback, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'

import {
  getAttribution,
  mintClickId,
  normalizePhone,
  sha256Hex,
} from '../lib/attribution'
import { track } from '../lib/analytics'
import { submitWhatsAppLead } from '../lib/leads'

const PREVIEW_URL = 'https://8grams-wiki-bucket.s3.ap-southeast-3.amazonaws.com/dropify-ebook/dropify-ebook-preview.pdf'

/** Programmatic download so we can fire it right after the phone is submitted. */
function triggerDownload() {
  const a = document.createElement('a')
  a.href = PREVIEW_URL
  a.download = ''
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * Gates the ebook "Download Preview" buttons behind a one-field phone form so a
 * preview opt-in can be sent to the CRM. This is not a WhatsApp key event or a
 * prerequisite for opening WhatsApp. Mounted once at the app root; it
 * intercepts clicks on any `[data-preview-download]` element, so the buttons
 * need no per-button wiring. Triggers the file download after the number is
 * submitted.
 *
 * SECURITY: the CTA elements must be <button>s, never <a href={PREVIEW_URL}> —
 * an anchor exposes the PDF URL in the DOM, and right-click / open-in-new-tab
 * bypasses this click interceptor entirely. The URL lives only in this module.
 */
export function EbookPreviewGate() {
  const [open, setOpen] = useState(false)
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef('')
  const submittedRef = useRef(false)

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return
      const target = event.target as HTMLElement | null
      const trigger = target?.closest('[data-preview-download]')
      if (!trigger) return

      event.preventDefault()
      setOpen(true)
      setPhone('')
      phoneRef.current = ''
      submittedRef.current = false
      setError(null)
      track('ebook_preview_gate_view', {
        page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      })
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  const dismiss = useCallback((reason: 'button' | 'escape' | 'backdrop') => {
    if (!submittedRef.current) {
      track('ebook_preview_gate_abandon', {
        page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
        dismiss_reason: reason,
        phone_entered: phoneRef.current.length > 0,
      })
    }
    setOpen(false)
    setError(null)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') dismiss('escape')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, dismiss])

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const e164 = normalizePhone(phone)
      if (!e164) {
        setError('Masukkan nomor WhatsApp yang valid, contoh 0812 3456 7890.')
        return
      }

      submittedRef.current = true
      // Fire the download synchronously within the click gesture.
      triggerDownload()

      const clickId = mintClickId()
      const attribution = getAttribution()
      const pagePath =
        typeof window !== 'undefined' ? window.location.pathname : undefined

      void sha256Hex(e164).then((phoneHash) => {
        const base = {
          click_id: clickId,
          phone_sha256: phoneHash,
          lead_source: 'ebook_preview',
          page_path: pagePath,
          ...attribution,
        }
        track('ebook_preview_download', { ...base })
        void submitWhatsAppLead({ data: { ...base, phone: e164 } }).catch(() => {})
      })

      setOpen(false)
      setError(null)
    },
    [phone],
  )

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/60 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ebook-preview-title"
      onClick={() => dismiss('backdrop')}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-green-700">
          <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
          Gratis
        </span>
        <h2 id="ebook-preview-title" className="mt-3 text-xl font-black text-slate-900">
          Unduh cuplikan ebook
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
          Masukkan nomor WhatsApp untuk menerima tautan cuplikan ebook.
        </p>

        <form onSubmit={handleSubmit} className="mt-4">
          <label
            htmlFor="ebook-preview-phone"
            className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500"
          >
            Nomor WhatsApp
          </label>
          <input
            ref={inputRef}
            id="ebook-preview-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            pattern="[0-9]*"
            value={phone}
            onChange={(event) => {
              const digits = event.target.value.replace(/\D/g, '')
              setPhone(digits)
              phoneRef.current = digits
              if (error) setError(null)
            }}
            placeholder="0812 3456 7890"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'ebook-preview-error' : undefined}
            className="w-full rounded-lg border border-slate-300 px-4 py-3 text-base text-slate-900 outline-none transition-colors focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          />
          {error && (
            <p id="ebook-preview-error" className="mt-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="mt-4 inline-flex w-full items-center justify-center gap-2.5 rounded-lg bg-green-700 px-6 py-3.5 text-base font-bold text-white shadow-sm transition-colors hover:bg-green-800"
          >
            <FontAwesomeIcon icon={faDownload} className="h-5 w-5" />
            Kirim tautan cuplikan
          </button>
          <button
            type="button"
            onClick={() => dismiss('button')}
            className="mt-2 w-full py-2 text-sm font-medium text-slate-400 transition-colors hover:text-slate-600"
          >
            Batal
          </button>
        </form>

        <p className="mt-3 text-center text-xs leading-relaxed text-slate-400">
          Nomor kamu dipakai untuk mengirim cuplikan dan tindak lanjut terkait ebook. Pengelolaannya mengikuti kebijakan privasi Dropify.
        </p>
      </div>
    </div>
  )
}
